import React, { useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRecoilValue } from 'recoil';

import { Image, Spinner } from 'components';
import { ButtonV3 } from 'components/ComponentV2';

import { useNotification } from 'hooks/useNotification';
import { API_ROUTES } from 'lib/api-routes';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { useAuth } from 'lib/providers/AuthProvider';

import { convertToBase64 } from 'utils/format';
import styles from './UploadCV.module.scss';

interface UploadCVProps {
  onNextClick: (isClicked?: boolean) => void;
}

export const UploadCV: React.FC<UploadCVProps> = ({
  onNextClick,
}: UploadCVProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [analyzing, setAnalyzing] = useState<boolean>(false);

  const { updateUserApiResponse } = useAuth();
  const [t] = useTranslation('candidate-onboarding');

  const xhrRef = useRef<XMLHttpRequest | null>(null);

  const notificationInstance = useNotification();
  const { NEXT_PUBLIC_API_HOST_URL } = process.env;

  const { headers } = useRecoilValue(httpHeadersState);

  const handleUploadProgress = (event: ProgressEvent) => {
    if (event.lengthComputable) {
      const percentCompleted = Math.round((event.loaded / event.total) * 100);
      setProgress(percentCompleted);
      if (percentCompleted === 100) {
        notificationInstance.acknowledge({
          message: t('upload-cv.notification-messages.uploaded'),
          variant: 'micro',
          borderRounded: true,
        });
        setAnalyzing(true);
      }
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setFile(file);
      if (file.size > 2097152) {
        notificationInstance.failure({
          message: t('upload-cv.notification-messages.file-size-error', {
            fileName: file.name,
          }),
          showClose: true,
          variant: 'medium',
          borderRounded: false,
          icon: 'icon_info',
        });
        return;
      }
      const fileExtension = file.name.split('.').pop() || '';
      if (!['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(fileExtension)) {
        notificationInstance.failure({
          message: t('upload-cv.notification-messages.file-type-error', {
            fileName: file.name,
          }),
          showClose: true,
          variant: 'medium',
          borderRounded: false,
          icon: 'icon_info',
        });
        return;
      }

      const fileInBase64String = await convertToBase64(file);
      const body = {
        candidate: {
          resume: {
            name: file.name,
            file: fileInBase64String,
          },
        },
      };

      try {
        setUploading(true);

        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;

        xhr.open(
          'PATCH',
          NEXT_PUBLIC_API_HOST_URL + API_ROUTES.CANDIDATE,
          true
        );

        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('X-Auth-Email', headers.get('X-Auth-Email') || '');
        xhr.setRequestHeader('X-Auth-Token', headers.get('X-Auth-Token') || '');

        xhr.onreadystatechange = function () {
          if (xhr.readyState === 4 && xhr.status === 200) {
            setUploading(false);
            const response = JSON.parse(xhr.response);
            updateUserApiResponse(response);
            notificationInstance.acknowledge({
              message: t('upload-cv.notification-messages.success-message'),
              showClose: true,
              borderRounded: false,
              variant: 'medium',
            });

            onNextClick();
          }
          if (xhr.readyState === 4 && xhr.status !== 200 && xhr.status !== 0) {
            setUploading(false);
            notificationInstance.failure({
              message: t('upload-cv.notification-messages.error-message'),
              showClose: true,
              borderRounded: false,
              variant: 'micro',
              icon: 'icon_info',
            });
          }
        };

        xhr.upload.onprogress = handleUploadProgress;
        xhr.send(JSON.stringify(body));
      } catch (error) {
        notificationInstance.failure({
          message: t('upload-cv.notification-messages.error-message'),
          showClose: true,
          borderRounded: false,
          variant: 'micro',
          icon: 'icon_info',
        });
        setUploading(false);
      }
    }
  };

  const handleCancel = () => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      setUploading(false);
    }
  };
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className={styles.uploadCVWrapper}>
      {!uploading ? (
        <div className={styles.uploadCVContent}>
          <Image
            src="/images/upload-resume-onboarding.png"
            alt="Upload Resume"
            className={styles.uploadCVImage}
          />
          <div className={styles.uploadCVDescription}>
            <p>
              {t('upload-cv.screen-1.text-1')}
              <span>{t('upload-cv.screen-1.text-2')}</span>
              <br />
              <br />
              {t('upload-cv.screen-1.text-3')}
            </p>
          </div>
          <div className={styles.uploadCVActionWrapper}>
            <ButtonV3
              variant="contained"
              color="primary"
              size="large"
              onClick={() => fileInputRef.current?.click()}
            >
              {t('upload-cv.screen-1.start-with-cv')}
            </ButtonV3>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt,.rtl"
            />
            <ButtonV3
              onClick={() => {
                onNextClick(true);
              }}
              variant="contained"
              color="secondary"
              size="large"
            >
              {t('upload-cv.screen-1.start-without-cv')}
            </ButtonV3>
          </div>
        </div>
      ) : analyzing ? (
        <>
          <div className={styles.uploadingWrapper}>
            <Spinner />
            <div className={styles.fileName}>
              {t('upload-cv.processing-screen.analyzing-title')}
            </div>
            <div className={styles.progress}>
              {t('upload-cv.processing-screen.analyzing-subtitle')}
            </div>
          </div>
        </>
      ) : (
        <div className={styles.uploadingWrapper}>
          <Spinner />
          <div className={styles.fileName}>{file?.name}</div>
          <div className={styles.progress}>
            {t('upload-cv.processing-screen.percent-uploaded', {
              percentage: progress,
            })}
          </div>
          <ButtonV3
            onClick={handleCancel}
            variant="contained"
            color="silent"
            size="small"
            startIcon="close"
          >
            {t('upload-cv.processing-screen.cancel')}
          </ButtonV3>
        </div>
      )}
    </div>
  );
};
